// ec-wizard.js — Step navigation, wizard state, Step 1 binding.
//
// Owns the single in-memory state object that every other ec-* module reads.
// Chunk 4 (appliances) and Chunk 5 (results) subscribe via EC_WIZARD.on().
//
// No framework, no dependencies beyond ec-core.js / ec-rates-config.js.

(function (root) {
  'use strict';

  // v2: the on-time-rebate flag became a late-payment-surcharge flag with the
  // opposite polarity (BERC 2026 has no on-time rebate). A v1 payload's
  // `onTime: true` would restore as "pays late" if read blind, so the key is
  // bumped and old payloads are simply ignored.
  var STORAGE_KEY = 'ec_wizard_state_v2';
  var TOTAL_STEPS = 3;

  var state = {
    step: 1,
    company: 'DESCO',
    category: 'LT-A',
    load: 2,
    // Drives the fixed monthly meter rent only. LT-A residential connections
    // are single-phase in practice, and that is the only phase we have a rent
    // figure for, so there is no control for this yet — it lives in state so
    // the day a three-phase rent is confirmed, one control is all it takes.
    phase: 'single', // 'single' | 'three'
    meter: 'postpaid', // 'postpaid' | 'prepaid'
    isLate: false, // the user's raw preference; see effectiveLate(). Default =
                   // pays on time = the standard bill, no surcharge.
    appliances: [], // [{key, qty, hours}] — populated in Chunk 4
    touchedAppliances: false // has the user edited the preset yet?
  };

  // `recalc` is fired by ec-appliances.js after the appliance list changes and
  // the running total has been recomputed.
  //
  // It exists because appliance edits do NOT go through setState() — the list
  // is mutated in place and saved — so they never emit `change`. In the old
  // three-panel wizard that was fine: Step 3 was hidden and re-rendered on
  // arrival. In the scroll narrative the results are ALWAYS on screen, so they
  // have to be told. It is a separate event rather than a `change` because
  // ec-appliances.js itself subscribes to `change`, and emitting that from
  // inside recalc() would recurse forever.
  var listeners = { change: [], step: [], recalc: [] };
  var els = {};
  var booted = false;
  var furthestStep = 1; // highest step reached, gates progress-bar navigation

  /* ------------------------------------------------------------ utilities */

  function $(sel, scope) {
    return (scope || document).querySelector(sel);
  }

  function $$(sel, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(sel));
  }

  function emit(evt, payload) {
    (listeners[evt] || []).forEach(function (fn) {
      try {
        fn(payload, state);
      } catch (e) {
        // A broken subscriber must never take the wizard down with it.
        if (root.console) console.error('[ec-wizard] listener error on "' + evt + '"', e);
      }
    });
  }

  function rates() {
    return root.EC_RATES;
  }

  function i18n() {
    return root.EC_I18N;
  }

  /**
   * Resolve a distributor logo to a URL. `window.EC_LOGO_SRC` is a
   * Liquid-injected { id: asset_url } map — same trick as EC_I18N_SRC for the
   * locale files, since this snippet has no Liquid of its own to call
   * asset_url directly. Falls back to a repo-relative path so the dev harness
   * (which serves the whole repo root, see dev/serve.js) works unchanged.
   */
  function logoSrc(id, filename) {
    var map = root.EC_LOGO_SRC || {};
    return map[id] || ('../assets/' + filename);
  }

  /* --------------------------------------------------------------- state */

  /**
   * A prepaid meter is paid before the electricity is used, so there is no due
   * date to miss and the late-payment surcharge cannot apply. The raw `isLate`
   * preference is preserved so switching back to postpaid restores what the
   * user had chosen.
   */
  function effectiveLate() {
    return state.meter === 'prepaid' ? false : !!state.isLate;
  }

  /** Inputs for EC.calcBill(), minus units (Chunk 4 supplies those). */
  function getBillInputs(units) {
    return {
      units: units,
      company: state.company,
      category: state.category,
      load: state.load,
      phase: state.phase,
      isPrepaid: state.meter === 'prepaid',
      isLate: effectiveLate()
    };
  }

  function setState(patch, reason) {
    var changed = false;
    for (var k in patch) {
      if (Object.prototype.hasOwnProperty.call(patch, k) && state[k] !== patch[k]) {
        state[k] = patch[k];
        changed = true;
      }
    }
    if (changed) {
      save();
      emit('change', { reason: reason || 'set', patch: patch });
    }
    return changed;
  }

  function save() {
    try {
      if (!root.localStorage) return;
      root.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          company: state.company,
          category: state.category,
          load: state.load,
          phase: state.phase,
          meter: state.meter,
          isLate: state.isLate,
          appliances: state.appliances,
          touchedAppliances: state.touchedAppliances
        })
      );
    } catch (e) {
      /* private mode / quota — resume is a convenience, never a requirement */
    }
  }

  function restore() {
    try {
      if (!root.localStorage) return;
      var raw = root.localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      var saved = JSON.parse(raw);
      if (!saved || typeof saved !== 'object') return;
      // Only accept values that still exist in the current config — rates and
      // appliance lists change between deploys.
      if (saved.company && rates().companies[saved.company]) state.company = saved.company;
      // Validated against the FULL class taxonomy (Run 4 Phase 4), not just
      // the functional ones — a visitor who picked a non-LT-A class, saw the
      // "coming soon" notice, and came back later should still see their
      // actual selection restored, not silently reset to LT-A.
      if (saved.category && (rates().customerClasses || []).some(function (c) { return c.key === saved.category; })) {
        state.category = saved.category;
      }
      if (isFinite(parseFloat(saved.load)) && parseFloat(saved.load) > 0) {
        state.load = parseFloat(saved.load);
      }
      if (saved.phase === 'single' || saved.phase === 'three') state.phase = saved.phase;
      if (saved.meter === 'prepaid' || saved.meter === 'postpaid') state.meter = saved.meter;
      if (typeof saved.isLate === 'boolean') state.isLate = saved.isLate;
      if (Array.isArray(saved.appliances)) {
        // A custom selection (Run 4 Phase 3a) has no catalog entry by
        // definition — `getAppliance` legitimately returns null for
        // `custom_*` keys, so that check only applies to catalog picks.
        // Without this split, every custom appliance in a saved session was
        // silently dropped on reload while everything else survived.
        state.appliances = saved.appliances.filter(function (s) {
          if (!s) return false;
          if (s.custom) return typeof s.key === 'string' && s.key.length > 0;
          return root.EC && root.EC.getAppliance(s.key);
        });
      }
      state.touchedAppliances = !!saved.touchedAppliances;
    } catch (e) {
      /* corrupt payload — start clean rather than half-restored */
    }
  }

  function reset() {
    try {
      if (root.localStorage) root.localStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
    state.appliances = [];
    state.touchedAppliances = false;
    // Starting over means Steps 2 and 3 are no longer reachable by jumping.
    furthestStep = 1;
    emit('change', { reason: 'reset' });
    goTo(1);
  }

  /* ---------------------------------------------------------- navigation */

  function reducedMotion() {
    return !!(root.matchMedia && root.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /** Reflect the current act into the jump nav. Colour is never the only cue. */
  function paintProgress(target) {
    $$('[data-ec-progress-step]').forEach(function (item) {
      var n2 = parseInt(item.getAttribute('data-ec-progress-step'), 10);
      var stateName = n2 === target ? 'current' : n2 < target ? 'done' : 'todo';
      item.setAttribute('data-state', stateName);
      if (n2 === target) {
        item.setAttribute('aria-current', 'true');
      } else {
        item.removeAttribute('aria-current');
      }

      var btn = item.querySelector('[data-ec-progress-go]');
      if (btn) {
        // NARRATIVE MODE: everything is on one page, so nothing is ever locked.
        // A disabled jump button in a document you can simply scroll would be a
        // lie about what is reachable.
        btn.disabled = false;
        var note = btn.querySelector('[data-ec-progress-state]');
        if (note) {
          note.textContent = i18n().t(
            n2 === target ? 'nav.stateCurrent'
              : n2 < target ? 'nav.stateDone'
                : 'nav.stateTodo'
          );
        }
      }
    });
  }

  /**
   * Move to an act.
   *
   * NARRATIVE MODE (run 3, Phase 3): the three acts are all in the document at
   * once, so this SCROLLS to the target instead of hiding the other two. The
   * step machinery is otherwise untouched — `state.step` still tracks where the
   * user is, and the `step` event every other module subscribes to still fires,
   * which is why nothing downstream had to change.
   *
   * @param {object} [opts]
   * @param {boolean} [opts.silent] update state without scrolling or focusing
   * @param {boolean} [opts.fromScroll] the scroll-spy is reporting a position,
   *        so do NOT scroll again (that would fight the user's own scrolling)
   */
  function goTo(n, opts) {
    opts = opts || {};
    var target = Math.min(Math.max(1, n), TOTAL_STEPS);
    var previous = state.step;
    state.step = target;

    if (target > furthestStep) furthestStep = target;

    paintProgress(target);
    emit('step', { from: previous, to: target });

    if (opts.silent || opts.fromScroll) return target;

    var panel = $('[data-ec-step="' + target + '"]');

    // Focus the act's heading so keyboard and screen-reader users land where
    // the content is, not at the top of the document.
    var heading = panel && panel.querySelector('[data-ec-step-title]');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      try {
        heading.focus({ preventScroll: true });
      } catch (e) {
        heading.focus();
      }
    }

    if (panel && panel.scrollIntoView) {
      panel.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
    }

    return target;
  }

  function next() {
    if (state.step === 1 && !validateStep1()) return state.step;
    return goTo(state.step + 1);
  }

  function back() {
    return goTo(state.step - 1);
  }

  /* ------------------------------------------------------------ Step 1 UI */

  /**
   * Builds the Step 1 distributor picker: one hidden native radio + a styled
   * logo-chip label per company, driven by EC_RATES.distributors. Adding a
   * company there (with a matching `companies[id]` entry) is all it takes to
   * make it selectable here — same "driven entirely by config" contract the
   * old <select> had.
   */
  function populateCompanies() {
    if (!els.companyGroup) return;
    var conf = rates().companies;
    var order = rates().distributors || Object.keys(conf).map(function (id) {
      return { id: id, logo: '' };
    });
    var frag = document.createDocumentFragment();

    order.forEach(function (d) {
      var key = d.id;
      if (!conf[key]) return; // config drift guard — no orphan radio for a removed company

      var chip = document.createElement('label');
      chip.className = 'ec-distributor';

      var input = document.createElement('input');
      input.type = 'radio';
      input.name = 'ec-company';
      input.className = 'ec-distributor__input';
      input.value = key;
      input.setAttribute('data-ec-company', '');
      input.setAttribute('aria-describedby', 'ec-company-hint');
      input.addEventListener('change', function () {
        if (!input.checked) return;
        state.company = input.value;
        setState({ category: els.category ? els.category.value : state.category }, 'company');
        applyCategoryGate();
        emit('change', { reason: 'company' });
      });

      var tile = document.createElement('span');
      tile.className = 'ec-distributor__tile';

      var img = document.createElement('img');
      img.className = 'ec-distributor__logo';
      img.src = logoSrc(key, d.logo);
      img.alt = i18n().pick(conf[key].name) || key;
      img.loading = 'lazy';
      tile.appendChild(img);

      var check = document.createElement('span');
      check.className = 'ec-distributor__check';
      check.setAttribute('aria-hidden', 'true');

      chip.appendChild(input);
      chip.appendChild(tile);
      chip.appendChild(check);
      frag.appendChild(chip);
    });

    els.companyGroup.innerHTML = '';
    els.companyGroup.appendChild(frag);
    if (!conf[state.company]) state.company = order.length ? order[0].id : Object.keys(conf)[0];
    syncCompanyRadios();
  }

  /** Reflect state.company into the radio group (used on boot/restore/toggle). */
  function syncCompanyRadios() {
    $$('[data-ec-company]').forEach(function (input) {
      input.checked = input.value === state.company;
    });
  }

  /**
   * Run 4 Phase 4: the dropdown lists every real BERC LT class
   * (`EC_RATES.customerClasses`), not just the ones this tool can actually
   * price — a residential-only dropdown would misrepresent what BERC
   * regulates. Labels come from that national list, not from
   * `companies[x].categories` (which only ever holds 'LT-A', the one class
   * with real rate data). See `categoryFunctional()` for the gate that keeps
   * the other eight from producing a silently-wrong bill.
   */
  function populateCategories() {
    if (!els.category) return;
    var classes = rates().customerClasses || [];
    var frag = document.createDocumentFragment();
    classes.forEach(function (c) {
      var opt = document.createElement('option');
      opt.value = c.key;
      opt.textContent = i18n().pick(c) + ' (' + c.key + ')';
      frag.appendChild(opt);
    });
    els.category.innerHTML = '';
    els.category.appendChild(frag);
    if (!classes.some(function (c) { return c.key === state.category; })) {
      state.category = classes.length ? classes[0].key : 'LT-A';
    }
    els.category.value = state.category;
  }

  /**
   * Only 'LT-A' has real tariff data anywhere in ec-rates-config.js — this is
   * intentionally the SAME check EC.getCategory() itself resolves against,
   * so "the dropdown thinks this class works" can never drift from "the math
   * layer thinks this class works".
   */
  function categoryFunctional() {
    var cat = rates().companies[state.company] && rates().companies[state.company].categories[state.category];
    return !!cat;
  }

  /** Reflect state into the Step 1 controls (used on boot and after restore). */
  function syncStep1() {
    syncCompanyRadios();
    if (els.category) els.category.value = state.category;
    if (els.load) els.load.value = state.load;
    $$('[data-ec-meter]').forEach(function (input) {
      input.checked = input.value === state.meter;
    });
    if (els.late) els.late.checked = effectiveLate();
    applyLateRule();
    applyCategoryGate();
  }

  /**
   * Run 4 Phase 4: only 'LT-A' computes. Selecting any other BERC class shows
   * the "coming soon" notice and disables Step 1's own advance button —
   * rather than silently walking the user into a bill priced as if they were
   * residential. This does NOT lock the rest of the page: the narrative-mode
   * jump nav and scrolling stay unrestricted (DESIGN.md's "nothing is ever
   * locked" rule), and the pinned meter / Step 3 results handle the same gate
   * independently, since both are always live regardless of which step the
   * user has "advanced" to.
   */
  function applyCategoryGate() {
    var functional = categoryFunctional();
    if (els.categoryNotice) els.categoryNotice.hidden = functional;
    if (els.next1) els.next1.disabled = !functional;
  }

  /**
   * UI half of the meter rule: a prepaid meter is paid before the electricity
   * is used, so there is no due date to miss and the late-payment control is
   * removed from the flow rather than left to be ticked and silently ignored.
   * The maths layer stays unrestricted.
   */
  function applyLateRule() {
    var isPrepaid = state.meter === 'prepaid';
    if (els.lateField) els.lateField.hidden = isPrepaid;
    if (els.lateHint) els.lateHint.hidden = isPrepaid;
    if (els.late) {
      els.late.disabled = isPrepaid;
      els.late.checked = effectiveLate();
    }
    if (els.prepaidNote) els.prepaidNote.hidden = !isPrepaid;
  }

  function validateStep1() {
    var okLoad = true;
    var raw = els.load ? parseFloat(els.load.value) : state.load;
    if (!isFinite(raw) || raw <= 0) okLoad = false;

    if (els.loadError) els.loadError.hidden = okLoad;
    if (els.load) {
      els.load.setAttribute('aria-invalid', okLoad ? 'false' : 'true');
      if (!okLoad) {
        els.load.focus();
      }
    }

    // Belt-and-braces alongside the disabled button (Run 4 Phase 4): the
    // button being disabled is what a mouse/touch user actually hits, but
    // next() is also reachable via the jump nav and Enter-to-submit, so the
    // gate is re-checked here too.
    var okCategory = categoryFunctional();
    if (!okCategory && els.categoryNotice) els.categoryNotice.hidden = false;

    return okLoad && okCategory;
  }

  function bindStep1() {
    els.companyGroup = $('[data-ec-distributors]');
    els.category = $('[data-ec-category]');
    els.categoryNotice = $('[data-ec-category-notice]');
    els.next1 = $('[data-ec-step="1"] [data-ec-next]');
    els.load = $('[data-ec-load]');
    els.loadError = $('[data-ec-load-error]');
    els.late = $('[data-ec-late]');
    els.lateField = $('[data-ec-late-field]');
    els.lateHint = $('[data-ec-late-hint]');
    els.prepaidNote = $('[data-ec-prepaid-note]');

    populateCompanies();
    populateCategories();
    syncStep1();

    if (els.category) {
      els.category.addEventListener('change', function () {
        setState({ category: els.category.value }, 'category');
        applyCategoryGate();
      });
    }

    if (els.load) {
      els.load.addEventListener('input', function () {
        var v = parseFloat(els.load.value);
        setState({ load: isFinite(v) && v > 0 ? v : 0 }, 'load');
        if (isFinite(v) && v > 0 && els.loadError) {
          els.loadError.hidden = true;
          els.load.setAttribute('aria-invalid', 'false');
        }
      });
    }

    $$('[data-ec-meter]').forEach(function (input) {
      input.addEventListener('change', function () {
        if (!input.checked) return;
        state.meter = input.value;
        applyLateRule();
        save();
        emit('change', { reason: 'meter' });
      });
    });

    if (els.late) {
      els.late.addEventListener('change', function () {
        setState({ isLate: !!els.late.checked }, 'late');
      });
    }
  }

  function bindNav() {
    $$('[data-ec-next]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        next();
      });
    });
    $$('[data-ec-back]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        back();
      });
    });
    $$('[data-ec-restart]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        reset();
      });
    });
    $$('[data-ec-progress-go]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var n = parseInt(btn.getAttribute('data-ec-progress-go'), 10);
        if (!isFinite(n) || btn.disabled) return;
        // NARRATIVE MODE: no validation gate here. Every act is already in the
        // document and reachable by scrolling, so refusing to jump would only
        // make the button look broken. `next()` still validates — that is where
        // the user is asking to move on, and where the error belongs.
        goTo(n);
      });
    });
  }

  /* ------------------------------------------------------- narrative mode */

  /**
   * Scroll-spy: keep `state.step` and the jump nav in sync with what the user
   * is actually looking at.
   *
   * Deliberately LIGHT — an IntersectionObserver, not a scroll-linked
   * animation. The brief's risk note is that heavy scroll handlers are exactly
   * what fights a theme's own sticky header, and this has to survive Ella.
   * No scroll listener, no rAF loop, no scroll-driven transforms.
   */
  /**
   * Which act is current, given the set of acts crossing the reading line.
   *
   * Pure and exported so it can be tested in Node — the browser half of the
   * spy (does IntersectionObserver actually deliver?) cannot be verified in a
   * headless or background tab, so at minimum the decision it makes is under test.
   *
   * @param {Object} crossing map of actNumber -> true
   * @param {number} previous the act to keep when nothing is on the line
   */
  function pickAct(crossing, previous) {
    var live = Object.keys(crossing || {})
      .map(Number)
      .filter(function (n) { return isFinite(n); })
      .sort(function (a, b) { return a - b; });

    // Very top or very bottom of the page: nothing crosses the line. Keep the
    // last known act rather than snapping somewhere arbitrary.
    if (!live.length) return previous;

    // Between two acts, the later one is the one being scrolled into.
    return live[live.length - 1];
  }

  function bindScrollSpy() {
    if (!root.IntersectionObserver) return; // no spy, everything still works

    var acts = $$('[data-ec-step]');
    if (!acts.length) return;

    // A THIN READING LINE across the middle of the viewport, not a band with a
    // ratio comparison. `intersectionRatio` is the fraction of the TARGET that
    // is visible, so an act taller than the viewport can never score highly and
    // a short act wins the moment it peeks in — the spy would stick on act 1
    // forever. Whichever act crosses this line simply IS the current one, so
    // there is nothing to compare and it flips exactly at the boundary.
    var crossing = {};

    var io = new root.IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var n = parseInt(entry.target.getAttribute('data-ec-step'), 10);
          if (entry.isIntersecting) crossing[n] = true;
          else delete crossing[n];
        });

        var current = pickAct(crossing, state.step);
        if (current !== state.step) goTo(current, { fromScroll: true });
      },
      { threshold: 0, rootMargin: '-45% 0px -45% 0px' }
    );

    acts.forEach(function (a) { io.observe(a); });
  }

  /**
   * Reveal each act as it comes into view. One class, one transition, defined
   * in CSS — no scroll-linked animation.
   *
   * Everything is revealed immediately and the observer is skipped entirely
   * when the user prefers reduced motion, or when IntersectionObserver is
   * missing. Content is NEVER hidden by JS that might not run: the CSS starts
   * revealed and this only adds the animated-in state.
   */
  function bindReveals() {
    var targets = $$('[data-ec-reveal]');
    if (!targets.length) return;

    function revealAll() {
      targets.forEach(function (t) { t.setAttribute('data-ec-reveal', 'in'); });
    }

    if (!root.IntersectionObserver || reducedMotion()) {
      revealAll();
      return;
    }

    // The CSS only hides a pending target while this flag is set, and the flag
    // is set from JS — so a page whose JS never ran shows everything at full
    // opacity rather than a blank column. Content must never be invisible by
    // default.
    var wizard = $('[data-ec-wizard]');
    if (wizard) wizard.setAttribute('data-ec-reveals', 'on');

    var io = new root.IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.setAttribute('data-ec-reveal', 'in');
          io.unobserve(entry.target); // reveal once; this is not a scroll effect
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -8% 0px' }
    );

    targets.forEach(function (t) { io.observe(t); });

    // FAILSAFE. An observer that never fires — a tab that loads in the
    // background, an ancestor that is display:none at observe time, a browser
    // quirk — would otherwise leave the page permanently blank. Nothing on this
    // page is allowed to depend on a scroll effect succeeding.
    setTimeout(function () {
      revealAll();
      io.disconnect();
    }, 2500);
  }

  /**
   * The pinned meter degrades to a normal in-flow block if `position: sticky`
   * is unavailable — or if a host theme has broken it, which is the real risk
   * here (an ancestor with `overflow: hidden` silently kills sticky).
   *
   * Feature-detect only; never throw. The rail is styled sticky in CSS and this
   * marks it unsupported so the CSS can drop back.
   */
  function applyStickySupport() {
    var rail = $('[data-ec-rail]');
    if (!rail) return;

    var ok = false;
    try {
      ok = !!(root.CSS && root.CSS.supports && root.CSS.supports('position', 'sticky'));
    } catch (e) {
      ok = false;
    }
    rail.setAttribute('data-ec-sticky', ok ? 'on' : 'off');
  }

  /* ----------------------------------------------------------------- boot */

  function init() {
    if (booted) return EC_WIZARD;
    if (!$('[data-ec-wizard]')) return EC_WIZARD; // not on this page
    if (!root.EC_RATES || !root.EC) {
      if (root.console) console.error('[ec-wizard] ec-rates-config.js / ec-core.js must load first.');
      return EC_WIZARD;
    }
    booted = true;
    restore();
    bindStep1();
    bindNav();
    applyStickySupport();
    // Resume lands on the act the user left, but never on a hard scroll jump
    // from a cold load — that reads as the page being broken.
    goTo(state.step, { silent: true });
    bindScrollSpy();
    bindReveals();

    // The company/category <option> text is authored by JS, so it is
    // NOT covered by ec-i18n.js's [data-i18n] sweep — re-populate on toggle.
    if (root.EC_I18N && root.EC_I18N.onChange) {
      root.EC_I18N.onChange(function () {
        populateCompanies();
        populateCategories();
        syncStep1();
        paintProgress(state.step);
      });
    }

    emit('change', { reason: 'init' });
    return EC_WIZARD;
  }

  function boot() {
    if (root.EC_I18N && root.EC_I18N.whenReady) root.EC_I18N.whenReady(init);
    else init();
  }

  var EC_WIZARD = {
    init: init,
    state: state,
    setState: setState,
    goTo: goTo,
    next: next,
    back: back,
    reset: reset,
    save: save,
    getBillInputs: getBillInputs,
    effectiveLate: effectiveLate,
    categoryFunctional: categoryFunctional,
    validateStep1: validateStep1,
    syncStep1: syncStep1,
    pickAct: pickAct,
    TOTAL_STEPS: TOTAL_STEPS,
    on: function (evt, fn) {
      if (!listeners[evt]) listeners[evt] = [];
      listeners[evt].push(fn);
      return EC_WIZARD;
    },
    _$: $,
    _$$: $$,
    _emit: emit
  };

  root.EC_WIZARD = EC_WIZARD;

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = EC_WIZARD;
})(typeof globalThis !== 'undefined' ? globalThis : this);
